import * as React from "react";
import { Row, Col } from "react-bootstrap";
import { HardDrive, Activity } from "lucide-react";
import useAnalytics from "./useAnalytics";
import {
  KpiCard,
  DeviceStatusCard,
  ActiveAlertsCard,
  IntegrationAdoptionCard,
  VersionDistributionCard,
  OrganizationOverview,
} from "./components";
import { IKpiCard } from "./analytics.types";
import "./analytics.scss";
import useNotification from "../../components/notification/useNotification";

const Analytics = () => {
  const {
    isLoading,
    error,
    kpis,
    sortedOrgs,
    orgSortField,
    orgSortDirection,
    handleOrgSort,
    expandedOrgId,
    handleOrgExpand,
    expandedOrgDetail,
    orgAggregates,
    totalDevices,
    onlineDevices,
    offlineDevices,
    onlinePercentage,
    firmwareVersions,
    iotAgentVersions,
    alertsBySeverity,
    alertsBySource,
    totalActiveAlerts,
    fwUpdateStatus,
    iotUpdateStatus,
    integrations,
    failedAnalyticsMessage,
    dateRange,
    dateRangePresets,
    handlePresetChange,
    handleApplyCustomDateRange,
  } = useAnalytics();

  const { showWarningNotification } = useNotification();

  const lastShownErrorRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (
      !isLoading &&
      error &&
      failedAnalyticsMessage &&
      lastShownErrorRef.current !== failedAnalyticsMessage
    ) {
      showWarningNotification(failedAnalyticsMessage);
      lastShownErrorRef.current = failedAnalyticsMessage;
    }

    if (!error) {
      lastShownErrorRef.current = null;
    }
  }, [isLoading, error, failedAnalyticsMessage, showWarningNotification]);

  return (
    <div className="analytics">
      <div className="analytics-heading">
        <div className="heading-content">
          <h1>Analytics</h1>
        </div>
      </div>

      <div className="analytics-body">
        <div className="analytics-section">
          <Row className="kpi-row">
            {kpis.map((kpi: IKpiCard, index: number) => (
              <Col key={index} sm={6} xl={true}>
                <KpiCard kpi={kpi} />
              </Col>
            ))}
          </Row>
        </div>

        <div className="analytics-section">
          <Row>
            <Col lg={4}>
              <DeviceStatusCard
                totalDevices={totalDevices}
                onlineDevices={onlineDevices}
                offlineDevices={offlineDevices}
                onlinePercentage={onlinePercentage}
              />
            </Col>
            <Col lg={4}>
              <ActiveAlertsCard
                alertsBySeverity={alertsBySeverity}
                alertsBySource={alertsBySource}
                totalActiveAlerts={totalActiveAlerts}
              />
            </Col>
            <Col lg={4}>
              <IntegrationAdoptionCard integrations={integrations} />
            </Col>
          </Row>
        </div>

        <div className="analytics-section">
          <Row>
            <Col lg={6}>
              <VersionDistributionCard
                title="Firmware Version Distribution"
                icon={HardDrive}
                versions={firmwareVersions}
                updateStatus={fwUpdateStatus}
                barVariant="firmware"
              />
            </Col>
            <Col lg={6}>
              <VersionDistributionCard
                title="IoT Agent Version Distribution"
                icon={Activity}
                versions={iotAgentVersions}
                updateStatus={iotUpdateStatus}
                barVariant="iot"
              />
            </Col>
          </Row>
        </div>

        <div className="analytics-section">
          <OrganizationOverview
            sortedOrgs={sortedOrgs}
            orgSortField={orgSortField}
            orgSortDirection={orgSortDirection}
            onSort={handleOrgSort}
            expandedOrgId={expandedOrgId}
            onOrgExpand={handleOrgExpand}
            expandedOrgDetail={expandedOrgDetail}
            orgAggregates={orgAggregates}
            dateRange={dateRange}
            dateRangePresets={dateRangePresets}
            onPresetChange={handlePresetChange}
            onApplyCustomDateRange={handleApplyCustomDateRange}
          />
        </div>
      </div>
    </div>
  );
};

export default Analytics;
